import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getChannexUsage, listHotels, listUsers } from '@/lib/api/admin';
import { periodOf } from '@/lib/period';
import { deriveOrganizations, orgLabel, type OrgSummary } from './derive';

/**
 * The derived organization directory, shared by every screen that needs to
 * put a name next to an `organizationId`. Three queries, all cached; the
 * Channex report is optional — its failure costs names, not the list.
 */
export function useOrganizations() {
  const hotels = useQuery({ queryKey: ['admin', 'hotels'], queryFn: listHotels });
  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: listUsers });
  const period = periodOf(new Date());
  const channex = useQuery({ queryKey: ['admin', 'channex-usage', period], queryFn: () => getChannexUsage(period) });

  const orgs = useMemo(() => deriveOrganizations(hotels.data ?? [], users.data ?? [], channex.data), [hotels.data, users.data, channex.data]);
  const byId = useMemo(() => new Map(orgs.map((o) => [o.id, o])), [orgs]);
  const label = (id: string | null) => (id ? (byId.get(id) ? orgLabel(byId.get(id) as OrgSummary) : `Организация ${id.slice(0, 8)}`) : '—');

  return {
    orgs,
    byId,
    label,
    isPending: hotels.isPending || users.isPending,
    error: hotels.error ?? users.error,
    refetch: () => { hotels.refetch(); users.refetch(); channex.refetch(); },
  };
}
